import { useState } from 'react';
import {
  Box,
  Button as MuiButton,
  Chip,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import { useNavigate } from 'react-router';
import { PAGE_PATHS } from '../../constants';
import { ImportResult } from './types';
import InfoCallout from './InfoCallout';
import ImportImagesDialog from '@components/ImportImagesDialog/ImportImagesDialog';
import { useInfo } from '@hooks/useInfo';

interface ConfirmationStepProps {
  result: ImportResult;
  onImportAnother: () => void;
}

const emptyOr = (params: GridRenderCellParams) => {
  const str = params.value as string;
  return str || <Box component='span' sx={{ color: 'text.disabled' }}>—</Box>;
};

const EmploymentTypeCell = ({ params }: { params: GridRenderCellParams }) => {
  const { employmentTypeDisplayName } = useInfo();
  return employmentTypeDisplayName(params.value || null) ?? emptyOr(params);
};

const columns: GridColDef[] = [
  { field: 'id', headerName: '#', width: 60, sortable: false },
  {
    field: 'status',
    headerName: 'Status',
    width: 110,
    sortable: false,
    renderCell: () => (
      <Chip
        label='Imported'
        size='small'
        sx={{ bgcolor: 'rgba(16, 185, 129, 0.15)', color: '#15803d', fontWeight: 600, borderRadius: 1 }}
      />
    ),
  },
  { field: 'first_name', headerName: 'First name', width: 130, sortable: false },
  { field: 'last_name', headerName: 'Last name', width: 130, sortable: false },
  { field: 'email', headerName: 'Email', minWidth: 200, flex: 1, sortable: false },
  { field: 'phone_number', headerName: 'Phone Number', minWidth: 200, flex: 1, sortable: false },
  { field: 'role', headerName: 'Role', width: 100, sortable: false },
  { field: 'title', headerName: 'Title', width: 120, sortable: false, renderCell: emptyOr },
  {
    field: 'employment_type',
    headerName: 'Designation',
    width: 160,
    sortable: false,
    renderCell: (params) => <EmploymentTypeCell params={params} />,
  },
  { field: 'rank', headerName: 'Rank', width: 100, sortable: false, renderCell: emptyOr },
  { field: 'badge_number', headerName: 'Badge number', width: 130, sortable: false, renderCell: emptyOr },
  { field: 'armed_permission', headerName: 'Armed permission', width: 150, sortable: false, renderCell: emptyOr },
];

const ConfirmationStep: React.FC<ConfirmationStepProps> = ({ result, onImportAnother }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [photosDialogOpen, setPhotosDialogOpen] = useState(false);

  const gridRows = result.rows.map((row, index) => ({
    id: index + 1,
    status: 'Imported',
    ...row,
  }));

  return (
    <>
      <Paper variant='outlined' sx={{ borderRadius: 2, p: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>

          <InfoCallout
            icon={<CheckCircleOutlineIcon sx={{ mt: '2px', mr: 1 }} />}
            title='Import complete'
            description={`${result.rowCount} ${result.rowCount === 1 ? 'user' : 'users'} added to the platform.`}
          />

          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: theme.palette.blueGrey[50],
              p: 2.5,
            }}
          >
            <Typography variant='h6' sx={{
              fontWeight: 600
            }}>Next steps</Typography>
            <Typography
              variant='body2'
              sx={{
                color: 'text.secondary',
                mt: 0.5
              }}>
              Match profile photos to your imported users, or jump straight to the user list.
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <MuiButton
                variant='contained'
                startIcon={<ImageOutlinedIcon />}
                endIcon={<ArrowForwardIcon />}
                sx={{ textTransform: 'none', borderRadius: 2 }}
                onClick={() => setPhotosDialogOpen(true)}
              >
                Manage photos
              </MuiButton>
              <MuiButton
                variant='outlined'
                startIcon={<PeopleOutlinedIcon />}
                endIcon={<ArrowForwardIcon />}
                sx={{ textTransform: 'none', borderRadius: 2 }}
                onClick={() => navigate(PAGE_PATHS.users)}
              >
                Go to users
              </MuiButton>
            </Box>
          </Box>

          <Box>
            <Typography variant='h6' sx={{
              fontWeight: 600
            }}>What was loaded</Typography>
            <Typography
              variant='body2'
              sx={{
                color: 'text.secondary',
                mt: 0.5
              }}>
              A read-only summary of the rows from your CSV upload.
            </Typography>
          </Box>

          <DataGrid
            rows={gridRows}
            columns={columns}
            autoHeight
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10 },
              },
            }}
            disableColumnFilter
            disableColumnMenu
            disableColumnResize
            disableRowSelectionOnClick
            hideFooterSelectedRowCount
            sx={{ borderRadius: 2 }}
          />

          <Box sx={{ pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
            <MuiButton sx={{ textTransform: 'none' }} size='small' onClick={onImportAnother}>
              Import another CSV
            </MuiButton>
          </Box>

        </Box>
      </Paper>

      <ImportImagesDialog
        open={photosDialogOpen}
        onClose={() => setPhotosDialogOpen(false)}
      />
    </>
  );
};

export default ConfirmationStep;
